import React, {useCallback, useState} from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Linking,
  KeyboardAvoidingView,
} from 'react-native';
import {
  Text,
  TextInput,
  Button,
  Snackbar,
  Divider,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useForm, Controller} from 'react-hook-form';
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import Geolocation from '@react-native-community/geolocation';
import {useRegistrationStore} from '../../store/useRegistrationStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {useAuthStore} from '../../store/useAuthStore';
import {colors, spacing} from '../../theme';
import StepIndicator from '../../components/StepIndicator';
import StatePickerModal from '../../components/StatePickerModal';
import {getStatesForCountry} from '../../data/states';

const ProgressBar = ({step, total, t}) => (
  <View style={styles.progressContainer}>
    <Text style={styles.progressText}>
      {t('store.stepOf', {step, total})}
    </Text>
    <View style={styles.progressBarTrack}>
      <View
        style={[styles.progressBarFill, {width: `${(step / total) * 100}%`}]}
      />
    </View>
  </View>
);

const SectionHeader = ({title}) => (
  <Text style={styles.sectionHeader}>{title}</Text>
);

const schema = yup.object().shape({
  latitude: yup.string(),
  longitude: yup.string(),
  addressLine1: yup.string(),
  addressLine2: yup.string(),
  area: yup.string(),
  city: yup.string(),
  state: yup.string(),
  country: yup.string(),
});

const StoreSetupStep2Screen = ({navigation, route}) => {
  const {t} = useTranslation();
  const {countryIso} = useRegistrationStore();
  const createOrgUnit = useOrgUnitStore(state => state.createOrgUnit);
  const uploadOrgUnitImages = useOrgUnitStore(state => state.uploadOrgUnitImages);
  const setHasStore = useAuthStore(state => state.setHasStore);
  const resetRegistration = useRegistrationStore(state => state.reset);

  const {
    storeName,
    storePhone,
    storeEmail,
    whatsappPhone,
    website,
    instagram,
    logoImage,
    photoImage,
  } = route.params || {};

  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');
  const [fetchingLocation, setFetchingLocation] = useState(false);
  const [stateModalVisible, setStateModalVisible] = useState(false);

  const availableStates = getStatesForCountry(countryIso);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: {errors},
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      latitude: '',
      longitude: '',
      addressLine1: '',
      addressLine2: '',
      area: '',
      city: '',
      state: '',
      country: countryIso || '',
    },
  });

  const handleOpenMap = useCallback(() => {
    const lat = watch('latitude') || '0';
    const lng = watch('longitude') || '0';
    const url = Platform.select({
      ios: `maps:0,0?q=${lat},${lng}`,
      android: `geo:${lat},${lng}?q=${lat},${lng}`,
    });
    Linking.openURL(url).catch(() => {
      Linking.openURL(`https://www.google.com/maps?q=${lat},${lng}`);
    });
  }, [watch]);

  const handleFetchLocation = useCallback(() => {
    setFetchingLocation(true);
    Geolocation.getCurrentPosition(
      position => {
        setValue('latitude', String(position.coords.latitude));
        setValue('longitude', String(position.coords.longitude));
        setFetchingLocation(false);
      },
      error => {
        setSnackbar(error.message || t('common.error'));
        setFetchingLocation(false);
      },
      {enableHighAccuracy: true, timeout: 15000, maximumAge: 10000},
    );
  }, [setValue, t]);

  const onSubmit = async data => {
    setSaving(true);
    try {
      const payload = {
        name: storeName,
        code: 'MAIN',
        orgType: 'OUTLET',
        phone: storePhone,
        email: storeEmail,
        whatsappPhone: whatsappPhone || '',
        website: website || '',
        instagram: instagram || '',
        latitude: data.latitude ? parseFloat(data.latitude) : null,
        longitude: data.longitude ? parseFloat(data.longitude) : null,
        addressLine1: data.addressLine1?.trim() || '',
        addressLine2: data.addressLine2?.trim() || '',
        area: data.area?.trim() || '',
        city: data.city?.trim() || '',
        state: data.state?.trim() || '',
        country: data.country?.trim() || '',
      };

      const store = await createOrgUnit(payload);

      if (logoImage || photoImage) {
        await uploadOrgUnitImages(store.id, logoImage, photoImage);
      }

      setHasStore(true);
      resetRegistration();
    } catch (err) {
      setSnackbar(err.response?.data?.error?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        style={{flex: 1}}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <ScrollView
          style={styles.form}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}>
          <StepIndicator currentStep={5} />
          <ProgressBar step={2} total={2} t={t} />

          <SectionHeader title={t('store.locationAddress')} />

          {/* Open Map + Current Location */}
          <TouchableOpacity
            style={styles.mapTouchable}
            onPress={handleOpenMap}
            activeOpacity={0.7}>
            <View style={styles.mapContent}>
              <Text style={styles.mapIcon}>{'\uD83D\uDDFA\uFE0F'}</Text>
              <View style={{flex: 1}}>
                <Text style={styles.mapTitle}>{t('store.openMap')}</Text>
                <Text style={styles.mapSubtitle}>Tap to view on map</Text>
              </View>
              <TextInput.Icon icon="open-in-new" />
            </View>
          </TouchableOpacity>

          <Button
            mode="outlined"
            onPress={handleFetchLocation}
            loading={fetchingLocation}
            disabled={fetchingLocation}
            style={styles.locationButton}
            textColor={colors.primary}
            icon="crosshairs-gps">
            {t('store.useCurrentLocation')}
          </Button>

          {/* Latitude & Longitude */}
          <View style={styles.row}>
            <View style={styles.halfField}>
              <Controller
                control={control}
                name="latitude"
                render={({field: {onChange, onBlur, value}}) => (
                  <TextInput
                    label={t('store.latitude')}
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    mode="outlined"
                    style={styles.input}
                    keyboardType="numeric"
                    outlineColor={colors.border}
                    activeOutlineColor={colors.primary}
                  />
                )}
              />
            </View>
            <View style={styles.halfField}>
              <Controller
                control={control}
                name="longitude"
                render={({field: {onChange, onBlur, value}}) => (
                  <TextInput
                    label={t('store.longitude')}
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    mode="outlined"
                    style={styles.input}
                    keyboardType="numeric"
                    outlineColor={colors.border}
                    activeOutlineColor={colors.primary}
                  />
                )}
              />
            </View>
          </View>

          <Divider style={styles.divider} />

          {/* Address Fields */}
          <Controller
            control={control}
            name="addressLine1"
            render={({field: {onChange, onBlur, value}}) => (
              <TextInput
                label={t('store.addressLine1')}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                mode="outlined"
                style={styles.input}
                outlineColor={colors.border}
                activeOutlineColor={colors.primary}
              />
            )}
          />

          <Controller
            control={control}
            name="addressLine2"
            render={({field: {onChange, onBlur, value}}) => (
              <TextInput
                label={t('store.addressLine2')}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                mode="outlined"
                style={styles.input}
                outlineColor={colors.border}
                activeOutlineColor={colors.primary}
              />
            )}
          />

          <Controller
            control={control}
            name="area"
            render={({field: {onChange, onBlur, value}}) => (
              <TextInput
                label={t('store.area')}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                mode="outlined"
                style={styles.input}
                outlineColor={colors.border}
                activeOutlineColor={colors.primary}
              />
            )}
          />

          <Controller
            control={control}
            name="city"
            render={({field: {onChange, onBlur, value}}) => (
              <TextInput
                label={t('store.city')}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                mode="outlined"
                style={styles.input}
                outlineColor={colors.border}
                activeOutlineColor={colors.primary}
              />
            )}
          />

          <Controller
            control={control}
            name="state"
            render={({field: {onChange, value}}) =>
              availableStates ? (
                <TouchableOpacity activeOpacity={0.7} onPress={() => setStateModalVisible(true)}>
                  <View pointerEvents="none">
                    <TextInput
                      label={t('store.state')}
                      value={value}
                      mode="outlined"
                      style={styles.input}
                      outlineColor={colors.border}
                      activeOutlineColor={colors.primary}
                      editable={false}
                      right={<TextInput.Icon icon="chevron-down" />}
                    />
                  </View>
                </TouchableOpacity>
              ) : (
                <TextInput
                  label={t('store.state')}
                  value={value}
                  onChangeText={onChange}
                  mode="outlined"
                  style={styles.input}
                  outlineColor={colors.border}
                  activeOutlineColor={colors.primary}
                />
              )
            }
          />

          <StatePickerModal
            visible={stateModalVisible}
            onClose={() => setStateModalVisible(false)}
            onSelect={selectedState => setValue('state', selectedState)}
            states={availableStates}
          />

          <Controller
            control={control}
            name="country"
            render={({field: {onChange, onBlur, value}}) => (
              <TextInput
                label={t('store.country')}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                mode="outlined"
                style={styles.input}
                outlineColor={colors.border}
                activeOutlineColor={colors.primary}
              />
            )}
          />

          <Button
            mode="contained"
            onPress={handleSubmit(onSubmit)}
            loading={saving}
            disabled={saving}
            style={styles.actionButton}
            buttonColor={colors.primary}
            contentStyle={styles.buttonContent}>
            {t('registration.finish')}
          </Button>

          <View style={styles.bottomSpacer} />
        </ScrollView>
      </KeyboardAvoidingView>

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}
        action={{label: t('common.ok'), onPress: () => setSnackbar('')}}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  form: {padding: spacing.md},
  progressContainer: {
    marginBottom: spacing.lg,
  },
  progressText: {
    fontSize: 13,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
    fontWeight: '500',
  },
  progressBarTrack: {
    height: 6,
    backgroundColor: colors.border,
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: colors.primary,
    borderRadius: 3,
  },
  sectionHeader: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.primary,
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  input: {marginBottom: spacing.sm, backgroundColor: colors.surface},
  divider: {
    marginVertical: spacing.md,
  },
  row: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  halfField: {
    flex: 1,
  },
  mapTouchable: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
    marginBottom: spacing.sm,
  },
  mapContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  mapIcon: {
    fontSize: 28,
  },
  mapTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.primary,
  },
  mapSubtitle: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 2,
  },
  locationButton: {
    marginBottom: spacing.md,
    borderColor: colors.primary,
    borderRadius: 8,
  },
  errorText: {color: colors.error, marginBottom: spacing.sm, fontSize: 12},
  actionButton: {
    marginTop: spacing.md,
    borderRadius: 8,
  },
  buttonContent: {
    paddingVertical: spacing.xs,
  },
  bottomSpacer: {
    height: spacing.xxl,
  },
});

export default StoreSetupStep2Screen;
